import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, IconButton } from "@mui/material";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import React, { useState } from "react";
import { MdDragIndicator } from "react-icons/md";

import { useAppDispatch } from "../../store/hooks";
import { IPanel } from "../../features/settings/settings.types";
import { Panel } from "../../constants";
import { updateProductAnalyzerSettingsThunk } from "../../features/settings/settings.thunk";
import IOSSwitch from "../IOSSwitch";
// import SearchAgainIcon from "../../assets/search_again.svg";
// import ProductIcon from "../../assets/product.svg";
// import QuickInfoIcon from "../../assets/quick_info.svg";
// import RanksAndPricesIcon from "../../assets/ranks_and_prices.svg";
// import AlertIcon from "../../assets/alert.svg";
// import MarketplacesIcon from "../../assets/marketplaces.svg";
// import ChartIcon from "../../assets/chart_icon.svg";
// import BuyBoxAnalysisIcon from "../../assets/buy_box_analysis.svg";
// import AdditionalExpensesIcon from "../../assets/additional_expenses.svg";
// import SellerCentralIcon from "../../assets/seller_central.svg";
// import ProfitCalculatorIcon from "../../assets/profit_calculator_icon.svg";
// import VariationsIcon from "../../assets/variations.svg";
// import HistoryIcon from "../../assets/history_icon.svg";
// import RoiIcon from "../../assets/roi_icon.svg";
// import KeepaIcon from "../../assets/keepa_icon.svg";
// import NotesAndTagsIcon from "../../assets/notes_tags_icon.svg";
// import DiscountsIcon from "../../assets/discounts.svg";
// import OffersIcon from "../../assets/offers_icon.svg";
// import ASINStatisticIcon from "../../assets/asin_statistic.svg";

// const PANEL_ICONS: Record<Panel, string> = {
//   [Panel.SEARCH_AGAIN]: SearchAgainIcon,
//   [Panel.PRODUCT]: ProductIcon,
//   [Panel.QUICK_INFO]: QuickInfoIcon,
//   [Panel.RANKS_AND_PRICES]: RanksAndPricesIcon,
//   [Panel.ALERTS]: AlertIcon,
//   [Panel.MARKETPLACES]: MarketplacesIcon,
//   [Panel.CHARTS]: ChartIcon,
//   [Panel.BUY_BOX_ANALYSIS]: BuyBoxAnalysisIcon,
//   [Panel.ADDITIONAL_EXPENSES]: AdditionalExpensesIcon,
//   [Panel.SELLER_CENTRAL]: SellerCentralIcon,
//   [Panel.PROFIT_CALCULATOR]: ProfitCalculatorIcon,
//   [Panel.VARIATIONS]: VariationsIcon,
//   [Panel.HISTORY]: HistoryIcon,
//   [Panel.ROI]: RoiIcon,
//   [Panel.KEEPA]: KeepaIcon,
//   [Panel.NOTES_AND_TAGS]: NotesAndTagsIcon,
//   [Panel.DISCOUNTS]: DiscountsIcon,
//   [Panel.OFFERS]: OffersIcon,
//   [Panel.ASIN_STATISTIC]: ASINStatisticIcon,
// };

interface ISortableItemProps {
  id: Panel;
  title?: string;
  enabled?: boolean;
  activeId: Panel | null;
  onSwitchToggle: (id: Panel) => void;
}

const SortableItem = ({
  id,
  title,
  enabled,
  onSwitchToggle,
  activeId,
}: ISortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: activeId === id ? 1000 : "auto",
        height: "40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: "0 2px 10px 0 #9d999940",
        fontSize: "14px",
        fontWeight: 500,
        padding: "0 15px",
      }}
      {...attributes}
      {...listeners}
    >
      <Box display={"flex"} alignItems={"center"} color={"#000"}>
        <MdDragIndicator
          style={{
            color: "#757575",
            fontSize: "20px",
            marginRight: "8px",
          }}
        />
        {/* <Box
          component="img"
          src={PANEL_ICONS[id]}
          alt="Panel Icon"
          sx={{
            width: "20px",
            height: "20px",
            marginRight: "8px",
            display: "block",
            flexShrink: 0,
          }}
        /> */}
        {title}
      </Box>
      <IOSSwitch checked={enabled} onChange={() => onSwitchToggle(id)} />
    </Box>
  );
};

interface IDraggableInputsProps {
  data: IPanel[];
}

const DraggableInputs: React.FC<IDraggableInputsProps> = ({ data }) => {
  const dispatch = useAppDispatch();
  const [activeId, setActiveId] = useState<Panel | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragStart = (event: any) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = ({ active, over }: any) => {
    setActiveId(null);
    if (active.id !== over.id) {
      const oldIndex = data.findIndex((item) => item.id === active.id);
      const newIndex = data.findIndex((item) => item.id === over.id);
      dispatch(
        updateProductAnalyzerSettingsThunk({
          panelOrder: arrayMove(data, oldIndex, newIndex),
        }),
      );
    }
  };

  const handleSwitchToggle = (id: Panel) => {
    dispatch(
      updateProductAnalyzerSettingsThunk({
        panelOrder: data.map((item) => ({
          ...item,
          enabled: item.id === id ? !item.enabled : item.enabled,
        })),
      }),
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={data} strategy={verticalListSortingStrategy}>
        {data.map((panel) => (
          <SortableItem
            key={panel.id}
            id={panel.id}
            title={panel.title}
            enabled={panel.enabled}
            activeId={activeId}
            onSwitchToggle={handleSwitchToggle}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default DraggableInputs;
